/**
 * <ry-search-list>
 *
 * Always-visible, filterable, keyboard-navigable list.
 * Supports optional grouping via <ry-search-group>.
 *
 * Usage:
 * <ry-search-list placeholder="Search...">
 *   <ry-search-item value="grid">Grid</ry-search-item>
 *   <ry-search-item value="tabs">Tabs</ry-search-item>
 * </ry-search-list>
 *
 * Grouped:
 * <ry-search-list placeholder="Search...">
 *   <ry-search-group label="Layout">
 *     <ry-search-item value="grid">Grid</ry-search-item>
 *   </ry-search-group>
 * </ry-search-list>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

// --- Fuzzy match ---

interface FuzzyResult {
  score: number;
  indices: number[];
}

function fuzzyMatch(query: string, text: string): FuzzyResult | null {
  const lq = query.toLowerCase();
  const lt = text.toLowerCase();

  // Prefix match — best score
  if (lt.startsWith(lq)) {
    return { score: 1, indices: Array.from({ length: lq.length }, (_, i) => i) };
  }

  // Substring match — good score
  const subIdx = lt.indexOf(lq);
  if (subIdx >= 0) {
    return { score: 0.8, indices: Array.from({ length: lq.length }, (_, i) => subIdx + i) };
  }

  // Fuzzy match — chars in order with gaps
  const indices: number[] = [];
  let qi = 0;
  for (let ti = 0; ti < lt.length && qi < lq.length; ti++) {
    if (lt[ti] === lq[qi]) {
      indices.push(ti);
      qi++;
    }
  }
  if (qi < lq.length) return null;

  // Score: reward consecutive chars
  let consecutiveBonus = 0;
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] === (indices[i - 1] ?? 0) + 1) consecutiveBonus++;
  }
  const score = 0.3 + (consecutiveBonus / Math.max(indices.length - 1, 1)) * 0.4;
  return { score, indices };
}

function highlightText(text: string, indices: number[]): string {
  if (indices.length === 0) return escapeHtml(text);
  const set = new Set(indices);
  let result = '';
  let inMark = false;
  for (let i = 0; i < text.length; i++) {
    const matched = set.has(i);
    if (matched && !inMark) { result += '<mark>'; inMark = true; }
    if (!matched && inMark) { result += '</mark>'; inMark = false; }
    result += escapeHtml(text[i] ?? '');
  }
  if (inMark) result += '</mark>';
  return result;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// --- Placeholder elements ---

class RySearchItem extends HTMLElement {}
if (!customElements.get('ry-search-item')) {
  customElements.define('ry-search-item', RySearchItem);
}

class RySearchGroup extends HTMLElement {}
if (!customElements.get('ry-search-group')) {
  customElements.define('ry-search-group', RySearchGroup);
}

// --- Main component ---

interface ItemEntry {
  value: string;
  label: string;
  el: HTMLElement;
  order: number;
  groupEl: HTMLElement | null;
}

let searchListIdCounter = 0;

export class RySearchList extends RyElement {
  #listId = '';
  #highlightedIndex = -1;
  #highlightSource: 'keyboard' | 'mouse' = 'keyboard';
  #items: ItemEntry[] = [];

  setup(): void {
    this.#listId = `ry-search-list-${++searchListIdCounter}`;
    this.#transformMarkup();

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) {
      this.on(input, 'input', this.#handleInput);
    }
    // Listen on document so arrow keys work even after blur/escape
    this.on(document, 'keydown', this.#handleKeydown);
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'mouseover', this.#handleMouseover);
  }

  #transformMarkup(): void {
    const placeholder = this.getAttribute('placeholder') ?? 'Search...';
    const groups = [...this.querySelectorAll('ry-search-group')];
    const hasGroups = groups.length > 0;

    let itemIndex = 0;
    const items: ItemEntry[] = [];

    let listContent = '';

    if (hasGroups) {
      groups.forEach((group, gi) => {
        const label = group.getAttribute('label') ?? '';
        const groupItems = [...group.querySelectorAll('ry-search-item')];
        let groupContent = `
          <div data-ry-target="group-header" class="ry-search-list__group-header">${escapeHtml(label)}</div>
        `;
        groupItems.forEach(item => {
          const value = item.getAttribute('value') ?? item.textContent?.trim() ?? '';
          const itemLabel = item.textContent?.trim() ?? '';
          groupContent += `
            <div data-ry-target="item" class="ry-search-list__item"
                 role="option" id="${this.#listId}-item-${itemIndex}"
                 data-value="${escapeHtml(value)}" data-ry-order="${itemIndex}"
                 aria-selected="false">
              <span data-ry-target="item-label">${escapeHtml(itemLabel)}</span>
            </div>
          `;
          items.push({ value, label: itemLabel, el: null as unknown as HTMLElement, order: itemIndex, groupEl: null });
          itemIndex++;
        });
        listContent += `<div data-ry-target="group" class="ry-search-list__group" data-group-index="${gi}">${groupContent}</div>`;
      });
    } else {
      const flatItems = [...this.querySelectorAll('ry-search-item')];
      flatItems.forEach(item => {
        const value = item.getAttribute('value') ?? item.textContent?.trim() ?? '';
        const itemLabel = item.textContent?.trim() ?? '';
        listContent += `
          <div data-ry-target="item" class="ry-search-list__item"
               role="option" id="${this.#listId}-item-${itemIndex}"
               data-value="${escapeHtml(value)}" data-ry-order="${itemIndex}"
               aria-selected="false">
            <span data-ry-target="item-label">${escapeHtml(itemLabel)}</span>
          </div>
        `;
        items.push({ value, label: itemLabel, el: null as unknown as HTMLElement, order: itemIndex, groupEl: null });
        itemIndex++;
      });
    }

    this.innerHTML = `
      <div data-ry-target="search" class="ry-search-list__search">
        <input data-ry-target="input" class="ry-search-list__input"
               type="text" placeholder="${escapeHtml(placeholder)}"
               autocomplete="off"
               role="combobox" aria-expanded="true"
               aria-controls="${this.#listId}" aria-autocomplete="list">
      </div>
      <div data-ry-target="list" class="ry-search-list__list"
           role="listbox" id="${this.#listId}">
        ${listContent}
      </div>
      <div data-ry-target="empty" class="ry-search-list__empty" hidden>No results</div>
    `;

    // Wire up element references
    const allItems = this.$$<HTMLElement>('[data-ry-target="item"]');
    allItems.forEach((el, i) => {
      const entry = items[i];
      if (entry) {
        entry.el = el;
        entry.groupEl = el.closest('[data-ry-target="group"]') as HTMLElement | null;
      }
    });

    this.#items = items;
  }

  // --- Filter ---

  #handleInput = (): void => {
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (!input) return;
    const query = input.value.trim();
    this.#filterItems(query);
  };

  #filterItems(query: string): void {
    let visibleCount = 0;

    if (!query) {
      // Restore all items in original order
      this.#items.forEach(item => {
        item.el.removeAttribute('hidden');
        const labelSpan = item.el.querySelector('[data-ry-target="item-label"]');
        if (labelSpan) labelSpan.innerHTML = escapeHtml(item.label);
      });
      // Show all groups
      this.$$<HTMLElement>('[data-ry-target="group"]').forEach(g => g.removeAttribute('hidden'));
      visibleCount = this.#items.length;
      // Restore original order
      this.#restoreOrder();
    } else {
      // Score and filter
      const scored: Array<{ item: ItemEntry; result: FuzzyResult }> = [];
      this.#items.forEach(item => {
        const result = fuzzyMatch(query, item.label);
        if (result) {
          item.el.removeAttribute('hidden');
          const labelSpan = item.el.querySelector('[data-ry-target="item-label"]');
          if (labelSpan) labelSpan.innerHTML = highlightText(item.label, result.indices);
          scored.push({ item, result });
          visibleCount++;
        } else {
          item.el.setAttribute('hidden', '');
          const labelSpan = item.el.querySelector('[data-ry-target="item-label"]');
          if (labelSpan) labelSpan.innerHTML = escapeHtml(item.label);
        }
      });

      // Sort visible items by score (highest first)
      scored.sort((a, b) => b.result.score - a.result.score);

      // Reorder DOM — sort within groups or flat list
      const groupMap = new Map<HTMLElement | null, Array<{ item: ItemEntry; result: FuzzyResult }>>();
      scored.forEach(s => {
        const key = s.item.groupEl;
        const arr = groupMap.get(key) ?? [];
        arr.push(s);
        groupMap.set(key, arr);
      });

      groupMap.forEach((entries, groupEl) => {
        const container = groupEl ?? this.$('[data-ry-target="list"]');
        if (!container) return;
        entries.forEach(e => container.appendChild(e.item.el));
      });

      // Hide/show groups based on visible children
      this.$$<HTMLElement>('[data-ry-target="group"]').forEach(g => {
        const hasVisible = g.querySelector('[data-ry-target="item"]:not([hidden])');
        if (hasVisible) {
          g.removeAttribute('hidden');
        } else {
          g.setAttribute('hidden', '');
        }
      });
    }

    // Empty state
    const emptyEl = this.$('[data-ry-target="empty"]');
    if (emptyEl) {
      if (visibleCount === 0) {
        emptyEl.removeAttribute('hidden');
      } else {
        emptyEl.setAttribute('hidden', '');
      }
    }

    // Reset highlight
    this.#highlightedIndex = -1;
    this.$$('[data-ry-target="item"][data-highlighted]').forEach(el => {
      el.removeAttribute('data-highlighted');
    });

    this.emit('filter', { query, count: visibleCount });
  }

  #restoreOrder(): void {
    const list = this.$('[data-ry-target="list"]');
    if (!list) return;

    // Check if groups exist
    const groups = this.$$<HTMLElement>('[data-ry-target="group"]');
    if (groups.length > 0) {
      groups.forEach(g => {
        const items = [...g.querySelectorAll<HTMLElement>('[data-ry-target="item"]')];
        items.sort((a, b) => Number(a.dataset.ryOrder ?? 0) - Number(b.dataset.ryOrder ?? 0));
        items.forEach(el => g.appendChild(el));
      });
    } else {
      const items = [...list.querySelectorAll<HTMLElement>('[data-ry-target="item"]')];
      items.sort((a, b) => Number(a.dataset.ryOrder ?? 0) - Number(b.dataset.ryOrder ?? 0));
      items.forEach(el => list.appendChild(el));
    }
  }

  // --- Keyboard ---

  #handleKeydown = (event: KeyboardEvent): void => {
    // Don't capture if user is typing in another input/textarea
    const active = document.activeElement;
    const myInput = this.$('[data-ry-target="input"]');
    if (active && active !== myInput && active !== document.body &&
        (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ||
         (active as HTMLElement).isContentEditable)) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        this.#highlightNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        this.#highlightPrev();
        break;

      case 'Home':
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        this.#highlightIndex(0);
        break;

      case 'End': {
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        const visible = this.#visibleItems();
        this.#highlightIndex(visible.length - 1);
        break;
      }

      case 'Enter':
        if (this.#highlightedIndex >= 0) {
          event.preventDefault();
          const visible = this.#visibleItems();
          const item = visible[this.#highlightedIndex];
          if (item) this.#selectItem(item);
        }
        break;

      case 'Escape': {
        event.preventDefault();
        const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
        if (input) {
          input.value = '';
          input.blur();
        }
        this.#filterItems('');
        break;
      }

      default:
        // Printable char — focus the search input so typing filters
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
          if (input && active !== input) {
            input.focus();
            // Let the keypress flow through to the input naturally
          }
        }
    }
  };

  // --- Click / Hover ---

  #handleClick = (event: MouseEvent): void => {
    const target = event.target as Element;
    const itemEl = target.closest('[data-ry-target="item"]');
    if (itemEl instanceof HTMLElement && !itemEl.hasAttribute('hidden')) {
      this.#selectItem(itemEl);
    }
  };

  #handleMouseover = (event: MouseEvent): void => {
    const target = event.target as Element;
    const itemEl = target.closest('[data-ry-target="item"]');
    if (!(itemEl instanceof HTMLElement) || itemEl.hasAttribute('hidden')) return;
    const visible = this.#visibleItems();
    const index = visible.indexOf(itemEl);
    if (index >= 0) {
      this.#highlightSource = 'mouse';
      this.#highlightIndex(index);
    }
  };

  // --- Highlight ---

  #visibleItems(): HTMLElement[] {
    return this.$$<HTMLElement>('[data-ry-target="item"]:not([hidden])');
  }

  #highlightIndex(index: number): void {
    const visible = this.#visibleItems();
    if (index < 0 || index >= visible.length) return;

    this.$$('[data-ry-target="item"][data-highlighted]').forEach(el => {
      el.removeAttribute('data-highlighted');
    });

    const item = visible[index];
    if (!item) return;

    item.setAttribute('data-highlighted', '');
    if (this.#highlightSource === 'keyboard') {
      item.scrollIntoView({ block: 'center' });
    }
    this.#highlightedIndex = index;

    const input = this.$('[data-ry-target="input"]');
    if (input) {
      input.setAttribute('aria-activedescendant', item.id);
    }

    if (this.#highlightSource === 'keyboard') {
      const value = item.dataset.value ?? '';
      const labelSpan = item.querySelector('[data-ry-target="item-label"]');
      const label = labelSpan?.textContent?.trim() ?? '';
      this.emit('highlight', { value, label });
    }
  }

  #highlightNext(): void {
    const visible = this.#visibleItems();
    if (this.#highlightedIndex < 0) {
      this.#highlightIndex(this.#selectedVisibleIndex(visible));
      return;
    }
    const next = Math.min(this.#highlightedIndex + 1, visible.length - 1);
    this.#highlightIndex(next);
  }

  #highlightPrev(): void {
    if (this.#highlightedIndex < 0) {
      this.#highlightIndex(this.#selectedVisibleIndex(this.#visibleItems()));
      return;
    }
    const prev = Math.max(this.#highlightedIndex - 1, 0);
    this.#highlightIndex(prev);
  }

  #selectedVisibleIndex(visible: HTMLElement[]): number {
    const selectedEl = this.$('[data-ry-target="item"][aria-selected="true"]');
    if (selectedEl instanceof HTMLElement) {
      const idx = visible.indexOf(selectedEl);
      if (idx >= 0) return idx;
    }
    return 0;
  }

  // --- Selection ---

  #selectItem(itemEl: HTMLElement): void {
    const value = itemEl.dataset.value ?? '';
    const labelSpan = itemEl.querySelector('[data-ry-target="item-label"]');
    const label = labelSpan?.textContent?.trim() ?? '';

    // Update selected state
    this.$$('[data-ry-target="item"]').forEach(el => {
      el.setAttribute('aria-selected', 'false');
    });
    itemEl.setAttribute('aria-selected', 'true');
    this.setAttribute('value', value);

    // Clear search and restore full list with selection visible
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input && input.value) {
      input.value = '';
      this.#filterItems('');
    }

    // Scroll selected item into view in the sidebar (keyboard only)
    if (this.#highlightSource === 'keyboard') {
      itemEl.scrollIntoView({ block: 'center' });
    }

    this.emit('select', { value, label });
  }

  // --- Public API ---

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    const item = this.$<HTMLElement>(`[data-ry-target="item"][data-value="${val}"]`);
    if (item) this.#selectItem(item);
  }

  clear(): void {
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) input.value = '';
    this.#filterItems('');
  }
}

customElements.define('ry-search-list', RySearchList);
